/**
 * Client-side mirror of `server/permissions.js` — UI gating only; the server
 * still enforces RBAC on every mutating endpoint.
 *
 * Single-key API: every permission is a `resource.action` string. `usePermission`,
 * `can`, and the server's `assertCan` all accept the same string so there is no
 * room to swap arguments. The `PermissionKey` type gives you IDE autocomplete
 * and catches typos at compile time.
 *
 * =============================================================================
 *                            RBAC MATRIX — source of truth
 * =============================================================================
 *
 *                                         owner  admin  member
 *   organization.update                     X
 *   organization.delete                     X
 *   organization.complete_onboarding        X                     (Owner-only setup wizard)
 *
 *   product.create                          X                     (Owner-only)
 *   product.update                          X
 *   product.delete                          X
 *   product.view                            X      X      X
 *
 *   members.view                            X      X      X
 *   members.invite_admin                    X
 *   members.invite_member                   X      X
 *   members.remove                          X      X
 *   members.change_role                     X      X
 *
 *   commitment.view                         X      X      X
 *   commitment.create                       X      X
 *   commitment.update                       X      X
 *   commitment.archive                      X      X            (soft — sets archived_at)
 *   commitment.delete                       X                   (hard — Owner only)
 *
 *   worklog.view                            X      X      X
 *   worklog.incremental_refresh             X      X      X     (Incremental + Update Fix Versions)
 *   worklog.full_refresh                    X      X            (Full Update only)
 *   worklog.modify                          X      X            (manual worklog row edits)
 *
 *   team.view                               X      X      X
 *   team.modify                             X      X
 *   team.manage_org_team                    X            (Team tab owner zones + roster writes + suggestions)
 *
 *   profile.view                            X      X      X     (My Profile tab)
 *   settings.view_org                       X      X            (Project/Team/Integrations tabs)
 *   settings.modify                         X      X
 *   integrations.configure                  X            (org Jira token, SharePoint, settings APIs)
 *
 *   calendar.view                           X      X      X
 *   calendar.modify                         X      X
 *   calendar.purge_resync                   X      X      (SharePoint purge & re-sync)
 *   analytics.view                          X      X      X
 *   forecast.view                           X      X      X
 *   forecast.modify                         X      X
 *   releases.view                           X      X      X
 *   releases.modify                         X      X
 *
 *   audit.view                              X      X
 *   billing.view                            X            (invoice API)
 *   billing.modify                          X
 *
 *   reports.access                          X      X            (Reports page + /api/reports/*, /api/products/:id/logo, /api/jira/attachment-proxy)
 *   reports.preset.manage                   X      X            (Save/rename/delete Report presets)
 *
 *   onboarding.write_org                    X      X      X     (Atlas mission progress writes)
 *   onboarding.write_user                   X      X      X     (personal restart override)
 *   onboarding.reset                        X      X            (wipe org-wide Atlas state)
 *
 * =============================================================================
 */

import { isAdminProductMembershipEnforced } from './featureFlags';

export type Role = 'owner' | 'administrator' | 'member';

const OWNER_ONLY = ['owner'] as const;
const OWNER_ADMIN = ['owner', 'administrator'] as const;
const ALL_ROLES = ['owner', 'administrator', 'member'] as const;

export const PERMISSIONS = {
  // organization
  'organization.update': OWNER_ONLY,
  'organization.delete': OWNER_ONLY,
  'organization.complete_onboarding': OWNER_ONLY,

  // product — Owner-only create/update/delete
  'product.create': OWNER_ONLY,
  'product.update': OWNER_ONLY,
  'product.delete': OWNER_ONLY,
  'product.view': ALL_ROLES,

  // members
  'members.view': ALL_ROLES,
  'members.invite_admin': OWNER_ONLY,
  'members.invite_member': OWNER_ADMIN,
  'members.remove': OWNER_ADMIN,
  'members.change_role': OWNER_ADMIN,

  // commitment
  'commitment.view': ALL_ROLES,
  'commitment.create': OWNER_ADMIN,
  'commitment.update': OWNER_ADMIN,
  'commitment.archive': OWNER_ADMIN,
  'commitment.delete': OWNER_ONLY,

  // worklog
  'worklog.view': ALL_ROLES,
  'worklog.incremental_refresh': ALL_ROLES,
  'worklog.full_refresh': OWNER_ADMIN,
  'worklog.modify': OWNER_ADMIN,

  // team & settings
  'team.view': ALL_ROLES,
  'team.modify': OWNER_ADMIN,
  'team.manage_org_team': OWNER_ONLY,
  'profile.view': ALL_ROLES,
  'settings.view_org': OWNER_ADMIN,
  'settings.modify': OWNER_ADMIN,
  'integrations.configure': OWNER_ONLY,

  // feature areas
  'calendar.view': ALL_ROLES,
  'calendar.modify': OWNER_ADMIN,
  'calendar.purge_resync': OWNER_ADMIN,
  'analytics.view': ALL_ROLES,
  'forecast.view': ALL_ROLES,
  'forecast.modify': OWNER_ADMIN,
  'releases.view': ALL_ROLES,
  'releases.modify': OWNER_ADMIN,

  // audit & billing
  'audit.view': OWNER_ADMIN,
  'billing.view': OWNER_ONLY,
  'billing.modify': OWNER_ONLY,

  // reports (Owner/Admin only; member has no access to /reports)
  'reports.access': OWNER_ADMIN,
  'reports.preset.manage': OWNER_ADMIN,

  // Atlas onboarding-state writes (PR2)
  'onboarding.write_org': ALL_ROLES,
  'onboarding.write_user': ALL_ROLES,
  'onboarding.reset': OWNER_ADMIN,
} as const satisfies Record<string, readonly Role[]>;

/** Union of every permission string. `usePermission` / `can` only accept this type. */
export type PermissionKey = keyof typeof PERMISSIONS;

/**
 * Resolve the effective role used by `can` from raw membership.
 *
 * Under the 3-role model there is no product sub-role: a `member` is either
 * assigned to the product (effective 'member') or has no access at all.
 *
 * When `REACT_APP_ADMIN_PRODUCT_MEMBERSHIP_ENFORCED` is true, the same rule applies
 * to `administrator` as to `member`; owners are never product-scoped here.
 *
 * @param orgRole        'owner' | 'administrator' | 'member' | null
 * @param isProductMember true when a `product_members` row exists for this user+product.
 */
export function getEffectiveRole(
  orgRole: string | null | undefined,
  isProductMember: boolean = true
): Role | null {
  if (!orgRole) return null;
  if (orgRole === 'owner') return 'owner';
  if (orgRole === 'administrator') {
    if (isAdminProductMembershipEnforced()) {
      return isProductMember ? 'administrator' : null;
    }
    return 'administrator';
  }
  if (orgRole === 'member') return isProductMember ? 'member' : null;
  return null;
}

/**
 * Products shown in UI switchers and org settings when **`REACT_APP_ADMIN_PRODUCT_MEMBERSHIP_ENFORCED`**
 * is on: admins are scoped like members; owners keep full catalog access.
 *
 * Mirrors the server middleware / calendar flag behavior (`ADMIN_PRODUCT_MEMBERSHIP_ENFORCED`).
 */
export function getVisibleProductsForOrgUser<T extends { id: string }>(
  org:
    | {
        role?: string | null;
        products?: T[] | null;
        productMemberships?: Array<{ product_id: string }> | null;
      }
    | null
    | undefined
): T[] {
  if (!org?.products?.length) return [];
  const r = org.role;
  if (r === 'owner') return org.products;
  if (r === 'administrator' && !isAdminProductMembershipEnforced()) return org.products;
  const ids = new Set((org.productMemberships ?? []).map((m) => m.product_id));
  return org.products.filter((p) => ids.has(p.id));
}

export function can(effectiveRole: string | null | undefined, key: PermissionKey): boolean {
  if (!effectiveRole) return false;
  const allowed = PERMISSIONS[key];
  if (!allowed) return false;
  return (allowed as readonly string[]).includes(effectiveRole);
}
